/**
 * The neutral ramp, generated from the organization's primary colour.
 *
 * Surfaces, borders, body text and headings are all neutrals, so a brand that
 * only drives `brand-*` leaves the app reading in the default hue. The ramp
 * below keeps the saturation and lightness of the hand-tuned scale in
 * `tokens.css` and swaps in the primary's hue, which is what carries the brand
 * across the whole page.
 */

import { hexToHsl, hslToChannels } from './colorScale';

interface Tone {
  saturation: number;
  lightness: number;
}

/** The saturation of the default primary, the hue these tones were tuned for. */
const REFERENCE_SATURATION = 98;

export const NEUTRAL_STEPS = [50, 100, 200, 300, 400, 500, 600, 700, 800, 850, 900, 950] as const;
export type NeutralStep = (typeof NEUTRAL_STEPS)[number];

const LIGHT_TONES: Record<NeutralStep, Tone> = {
  50: { saturation: 46.7, lightness: 97.1 },
  100: { saturation: 45.5, lightness: 97.8 },
  200: { saturation: 38.8, lightness: 90.4 },
  300: { saturation: 29.0, lightness: 81.8 },
  400: { saturation: 25.8, lightness: 65.1 },
  500: { saturation: 17.7, lightness: 47.6 },
  600: { saturation: 19.0, lightness: 41.2 },
  700: { saturation: 17.3, lightness: 35.1 },
  800: { saturation: 16.9, lightness: 30.2 },
  850: { saturation: 42.9, lightness: 26.1 },
  900: { saturation: 81.8, lightness: 21.6 },
  950: { saturation: 79.3, lightness: 11.4 },
};

const DARK_TONES: Record<NeutralStep, Tone> = {
  50: { saturation: 48.8, lightness: 8.4 },
  100: { saturation: 43.8, lightness: 12.5 },
  200: { saturation: 35.2, lightness: 21.2 },
  300: { saturation: 31.9, lightness: 28.2 },
  400: { saturation: 17.7, lightness: 47.6 },
  500: { saturation: 25.8, lightness: 65.1 },
  600: { saturation: 26.1, lightness: 72.9 },
  700: { saturation: 26.3, lightness: 77.6 },
  800: { saturation: 29.0, lightness: 81.8 },
  850: { saturation: 32.1, lightness: 89.0 },
  900: { saturation: 41.7, lightness: 95.3 },
  950: { saturation: 45.5, lightness: 97.8 },
};

/**
 * Builds the ramp for one theme, or null when the colour will not parse.
 *
 * A muted primary damps the tint proportionally, so a grey brand yields grey
 * neutrals rather than a fully saturated ramp on whatever hue grey resolves to.
 */
export function generateNeutralScale(
  hex: string,
  dark: boolean,
): Record<NeutralStep, string> | null {
  const hsl = hexToHsl(hex);
  if (!hsl) return null;

  const damping = Math.min(hsl.saturation / REFERENCE_SATURATION, 1);
  const tones = dark ? DARK_TONES : LIGHT_TONES;

  const scale = {} as Record<NeutralStep, string>;
  for (const step of NEUTRAL_STEPS) {
    scale[step] = hslToChannels(hsl.hue, tones[step].saturation * damping, tones[step].lightness);
  }
  return scale;
}
